// React
import React, { Fragment, useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect as reduxConnect, useSelector } from 'react-redux'
import { compose } from 'redux'

// Libraries
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faShoppingCart as fasShoppingCart } from '@fortawesome/pro-solid-svg-icons'
import { faShoppingCart as farShoppingCart } from '@fortawesome/pro-regular-svg-icons'
import { faShoppingCart as falShoppingCart } from '@fortawesome/pro-light-svg-icons'
import { faShoppingCart as fatShoppingCart } from '@fortawesome/pro-thin-svg-icons'

import { StyleSheetManager } from 'styled-components'

// Components
import BMiniCartButton, { HostStyles } from './styles'
import MiniCart from 'shop-components/components/MiniCart'
import Popover from 'ui/components/Popover'
import PortalWrapper from 'ui/components/PortalWrapper'

// Shared
import registerCustomElement from 'shop-components/utils/registerCustomElement'
import withStore from 'shop-components/utils/withStore'
import withTheme from 'shop-components/utils/withTheme'
import { withTranslation } from 'shared/utils/withTranslation'
import { setUserFrameworkCartData } from 'shared/utils/userFrameworkEvents'
import ClientActions from 'store/v2/redux/actions/client'
import useIsMobile from 'shared/utils/useIsMobile'
import { shouldForwardProp } from 'shared/utils/shouldForwardProp'

const getIcon = (style) => {
  switch (style) {
    case 'solid':
      return fasShoppingCart
    case 'regular':
      return farShoppingCart
    case 'light':
      return falShoppingCart
    case 'thin':
      return fatShoppingCart
    default:
      return farShoppingCart
  }
}

const MiniCartButton = ({ cart, cartOpen, iconStyle, itemCount }) => {
  const isMobile = useIsMobile()
  const icon = getIcon(iconStyle)
  const isOpenPicker = useSelector((state) => state.client.modalPickerOpen)

  useEffect(() => {
    setUserFrameworkCartData()
  }, [cart])

  const toggleCart = (cartOpen) => {
    if (isOpenPicker) return
    return ClientActions.toggleCart(cartOpen)
  }

  const renderCartButton = () => {
    return (
      <Fragment>
        <HostStyles />
        <BMiniCartButton
          key="booqable-minicart-button"
          id="booqable-minicart-button"
          onClick={toggleCart.bind(null, !cartOpen)}
        >
          <FontAwesomeIcon icon={icon} size="lg" />
          {itemCount > 0 && <BMiniCartButton.Badge>{itemCount}</BMiniCartButton.Badge>}
        </BMiniCartButton>
      </Fragment>
    )
  }

  const renderMiniCart = () => {
    return (
      <StyleSheetManager target={document.body} shouldForwardProp={shouldForwardProp} enableVendorPrefixes>
        <MiniCart />
      </StyleSheetManager>
    )
  }

  if (isMobile) {
    return (
      <Fragment>
        <PortalWrapper open={cartOpen}>
          <StyleSheetManager target={document.body} shouldForwardProp={shouldForwardProp} enableVendorPrefixes>
            <MiniCart />
          </StyleSheetManager>
        </PortalWrapper>
        {renderCartButton()}
      </Fragment>
    )
  }

  return (
    <Popover
      open={cartOpen}
      onClose={toggleCart.bind(null, false)}
      method="manual"
      direction="bottom-end"
      content={renderMiniCart}
      useContentWrapper={false}
      zIndex={9999999}
    >
      {renderCartButton()}
    </Popover>
  )
}

MiniCartButton.displayName = 'MiniCartButton'

MiniCartButton.propTypes = {
  cart: PropTypes.object,
  cartOpen: PropTypes.bool,
  itemCount: PropTypes.number,
  iconStyle: PropTypes.oneOf(['solid', 'regular', 'light', 'thin'])
}

const mapStateToProps = (state) => {
  const cart = state.orm.carts.getById(state.client.cartId)
  const lines = state.orm.lines.getAll({ id: cart?.lines, product_type: ['consumable', 'sales_item', 'rental', 'bundle'] })
  const isOpenPicker = state.client.modalPickerOpen

  const itemCount = lines.map((line) => line.quantity).reduce((a, b) => a + b, 0)

  return {
    cart,
    cartOpen: state.client.cartOpen,
    itemCount,
    isOpenPicker
  }
}

const decoratedComponent = compose(
  withTranslation('user'),
  withTheme,
  withStore,
  reduxConnect(mapStateToProps, null)
)(MiniCartButton)

registerCustomElement('bq-minicart-button', decoratedComponent, {
  attributes: ['icon-style']
})

export default decoratedComponent
